import { Loader2 } from 'lucide-react'
import { Button } from './ui/button'

interface LoadingButtonProps extends React.ComponentProps<typeof Button> {
  loading?: boolean
  loadingText?: string
}

/**
 * A Button that shows a spinner and optional loading text while a mutation is in-flight.
 *
 * When `loading` is true the entire children are replaced with a spinning
 * `Loader2` icon plus the `loadingText` (if provided). The button is also
 * automatically disabled while loading.
 *
 * Usage:
 *
 * ```tsx
 * // Text button with icon
 * <LoadingButton loading={mutation.isPending} loadingText="Saving..." size="sm">
 *   <Check /> Save
 * </LoadingButton>
 *
 * // Icon-only button (no loadingText — just shows spinner)
 * <LoadingButton loading={mutation.isPending} variant="ghost" size="icon">
 *   <Trash2 />
 * </LoadingButton>
 * ```
 */
function LoadingButton({
  loading,
  loadingText,
  children,
  disabled,
  ...props
}: LoadingButtonProps) {
  return (
    <Button disabled={disabled || loading} {...props}>
      {loading ? (
        <>
          <Loader2 className="animate-spin" />
          {loadingText}
        </>
      ) : (
        children
      )}
    </Button>
  )
}

export { LoadingButton }
